import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, message, Divider, Tooltip } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getDatabase, update, ref } from "firebase/database";
import { getStorage } from "firebase/storage";
import Firebase from "../../Firebase/Firebase";
import { useAuth } from "../../AuthContext/AuthContext";

const { TextArea } = Input;
const db = getDatabase(Firebase);
const storage = getStorage(Firebase);

export const typeFieldsMap = {
  title: [
    { name: "title", label: "Enter Title", component: <Input />, isRequired: true },
  ],
  text: [
    { name: "header", label: "Header", component: <Input />, isRequired: false },
    { name: "body", label: "Body", component: <TextArea rows={4} />, isRequired: true },
    { name: "audioFile", label: "Audio File", isFile: true },
  ],
  link: [
    { name: "header", label: "Header", component: <Input />, isRequired: true },
    { name: "linkDescription", label: "Links Description", component: <Input />, isRequired: true },
    {
      name: "links",
      label: "Links",
      isArray: true,
      fields: [
        { name: "linkPath", label: "Link URL", component: <Input />, isRequired: true },
        { name: "linkTitle", label: "Link Title", component: <Input />, isRequired: true },
      ]
    },
    { name: "audioFile", label: "Audio File", isFile: true },
  ],
  image: [
    { name: "header", label: "Header", component: <Input /> },
    { name: "body", label: "Body", component: <TextArea rows={4} />, isRequired: false },
    { name: "imagePath", label: "Image File", isFile: true, isRequired: true },
    { name: "audioFile", label: "Audio File", isFile: true, isRequired: false },
  ],
  animation: [
    { name: "header", label: "Header", component: <Input />, isRequired: true },
    { name: "body", label: "Body", component: <TextArea rows={4} />, isRequired: false },
    { name: "animationPath", label: "Animation File", isFile: true, isRequired: true },
  ],
  focus: [
    { name: "header", label: "Header", component: <Input />, isRequired: true },
    { name: "body", label: "Body", component: <TextArea rows={4} />, isRequired: true },
    { name: "audioFile", label: "Audio File", isFile: true, isRequired: false },
  ],
  activity: [
    { name: "target", label: "Target", component: <Input />, isRequired: true },
  ],
  pdf: [
    { name: "header", label: "Header", component: <Input />, isRequired: true },
    { name: "body", label: "Body", component: <TextArea rows={4} />, isRequired: false },
    { name: "pdf", label: "PDF File", isFile: true, isRequired: true, accept: ".pdf" },
    { name: "audioFile", label: "Audio File", isFile: true, isRequired: false },
  ],
};

const AddLessonComponent = ({ id, snapshot, pageTo }) => {
  const { canAccessModule } = useAuth();
  const canUpdateLesson = canAccessModule("LESSONS", "update");
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [currentPages, setCurrentPages] = useState([]);

  useEffect(() => {
    if (snapshot && snapshot.exists()) {
      const lessonData = snapshot.val();
      const pages = lessonData.pages || [];
      setCurrentPages(pages);
    }
  }, [snapshot]);

  const handleTypeChange = (value) => {
    setSelectedType(value);
    form.setFieldsValue({ type: value });
  };

  const uploadFileAndGetURL = async (file) => {
    if (!file) return null;
    const fileName = `${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, `System/Lessons/${id}/${fileName}`);
    const snapshot = await uploadBytes(fileRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleFinish = async (values) => {
    if (!canUpdateLesson) {
      message.error("You do not have permission to update lessons.");
      return;
    }

    try {
      setUploading(true);
      let componentData = { type: values.type };
      const typeFields = typeFieldsMap[values.type] || [];

      for (const field of typeFields) {
        if (field.isArray) continue;

        if (values[field.name] !== undefined && values[field.name] !== "") {
          if (field.isFile) {
            componentData[field.name] = await uploadFileAndGetURL(values[field.name]);
          } else {
            componentData[field.name] = values[field.name];
          }
        }
      }

      if (values.links) {
        componentData.links = values.links.map(link => ({
          linkPath: link.linkPath,
          linkTitle: link.linkTitle,
          offline: false
        }));
      }

      if (!snapshot || !snapshot.exists()) {
        throw new Error("Lesson not found");
      }

      const lessonData = snapshot.val();
      const currentPages = lessonData.pages || [];
      let targetPage = currentPages.find(
        (page) => Number(page.pageOrder) === Number(pageTo)
      );

      if (!targetPage) {
        targetPage = {
          pageName: values.pageName,
          pageOrder: Number(pageTo),
          components: [],
        };
        currentPages.push(targetPage);
      }

      targetPage.components = targetPage.components || [];
      const existingOrders = targetPage.components
        .filter(comp => comp.type === componentData.type)
        .map(comp => comp.order)
        .filter((order) => order !== undefined)
        .sort((a, b) => a - b);

      if (componentData.order === undefined) {
        let nextOrder = 0;
        while (existingOrders.includes(nextOrder)) {
          nextOrder++;
        }
        componentData.order = nextOrder;
      }
      else if (existingOrders.includes(componentData.order)) {
        let nextOrder = componentData.order + 1;
        while (existingOrders.includes(nextOrder)) {
          nextOrder++;
        }
        componentData.order = nextOrder;
      }

      const orderConflict = targetPage.components.some(
        comp => comp.type !== componentData.type && comp.order === componentData.order
      );

      if (orderConflict) {
        const allOrders = new Set(
          targetPage.components.map(comp => comp.order).filter(o => o !== undefined)
        );
        let nextOrder = componentData.order;
        while (allOrders.has(nextOrder)) {
          nextOrder++;
        }
        componentData.order = nextOrder;
      }

      targetPage.components.push(componentData);

      await update(ref(db, `talmidsdb/lessons/${id}`), { pages: currentPages });

      form.resetFields();
      setSelectedType(null);
      message.success("Component added successfully");
    } catch (error) {
      console.error("Failed to add lesson component:", error);
      message.error("Unable to add component: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const renderArrayField = (field) => {
    return (
      <Form.List name={field.name}>
        {(fields, { add, remove }) => (
          <div>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                {field.fields.map((subField) => (
                  <Form.Item
                    {...restField}
                    name={[name, subField.name]}
                    label={fields.length > 1 ? '' : subField.label}
                    rules={[{ required: true, message: `${subField.label} is required` }]}
                    style={{ flex: 1, marginRight: 8 }}
                  >
                    {subField.component || <Input placeholder={`Enter ${subField.label.toLowerCase()}`} />}
                  </Form.Item>
                ))}
                {fields.length > 0 && (
                  <Form.Item>
                    <MinusCircleOutlined
                      onClick={() => remove(name)}
                      style={{ color: 'red', marginTop: fields.length > 1 ? '0' : '32px' }}
                    />
                  </Form.Item>
                )}
              </div>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add Link
              </Button>
            </Form.Item>
          </div>
        )}
      </Form.List>
    );
  };

  const renderField = (field) => {
    if (field.isArray) {
      return renderArrayField(field);
    }

    if (field.isFile) {
      return (
        <Form.Item
          key={field.name}
          label={field.label}
          name={field.name}
          valuePropName="file"
          getValueFromEvent={(e) => (e.target.files ? e.target.files[0] : null)}
        >
          <Input
            type="file"
            accept={field.accept || "image/*,.pdf"}
            style={{ width: "100%" }}
          />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        key={field.name}
        label={field.label}
        name={field.name}
        rules={[{ required: field.isRequired, message: `${field.label} is required` }]}
      >
        {field.component}
      </Form.Item>
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <Form.Item
        label="Component Type"
        name="type"
        rules={[{ required: true, message: "Please select component type" }]}
      >
        <Select
          placeholder="Select component type"
          onChange={handleTypeChange}
          options={Object.keys(typeFieldsMap).map((type) => ({
            label: type.charAt(0).toUpperCase() + type.slice(1),
            value: type,
          }))}
        />
      </Form.Item>
      {selectedType &&
        typeFieldsMap[selectedType].map((field) => renderField(field))}
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={uploading}
          disabled={!canUpdateLesson}
        >
          Add Component
        </Button>
      </Form.Item>
    </Form>
  );
};

export default React.memo(AddLessonComponent);
